import React, { useState } from "react";
import {
    Table, Thead, Tbody, Tr, Th, Td, TableContainer, IconButton, Box, HStack, useToast, Heading, Flex, Button,
    Icon, Link
} from "@chakra-ui/react";
import { FaUserTimes, FaUserPlus, FaEdit, FaKey } from "react-icons/fa";
import { PhoneIcon } from "@chakra-ui/icons";

import ConfirmModal from "./ConfirmModal";
import CreateUserModal from "./CreateUserModal";
import UpdateUserModal from "./UpdateUserModal";
import ResetPasswordModal from "./ResetPasswordModal"; // ✅ import here
import useUsers from "../../hooks/useUsers";
import { stickyColHeaderProps, stickyColCellProps } from "../../constants/tableStyles";

const UserTable = ({ locationId }) => {
    const { users, loading, handleDeactivate, handleCreateUser, handleUpdateUser } = useUsers(locationId);
    const [selectedUser, setSelectedUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isResetModalOpen, setIsResetModalOpen] = useState(false); // ✅ state for reset modal
    const toast = useToast();

    const openModal = (user) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const openUpdateModal = (user) => {
        setSelectedUser(user);
        setIsUpdateModalOpen(true);
    };

    const openResetPasswordModal = (user) => {
        setSelectedUser(user);
        setIsResetModalOpen(true);
    };

    const confirmDeactivation = async () => {
        if (selectedUser) {
            await handleDeactivate(selectedUser.id);
            setIsModalOpen(false);
            toast({
                title: "User Deactivated",
                description: `${selectedUser.fullName} je deaktiviran.`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <>
            <Flex
                justify="space-between"
                align="center"
                mb={5}
                pl={4}
                pr={4}
            >
                <Heading size="lg" color="brand.700">Upravljanje korisnicima</Heading>
                <Button bg="brand.600" color="white" boxShadow="soft" _hover={{ bg: "brand.700" }} onClick={() => setIsCreateModalOpen(true)}>
                    <FaUserPlus />
                </Button>
            </Flex>

            {loading ? <p>Učitavanje korisnika...</p> : (
                <Box
                    bg="white"
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="gray.100"
                    boxShadow="card"
                    p={3}
                    mx={4}
                >
                <TableContainer overflowX="auto">
                    <Table variant="striped" colorScheme="gray" size="sm">
                        <Thead>
                            <Tr>
                                <Th {...stickyColHeaderProps}>#</Th>
                                <Th>Naziv</Th>
                                <Th>Email</Th>
                                <Th>Telefonski broj</Th>
                                <Th>Akcije</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {users.map((user, index) => (
                                <Tr key={user.id} _hover={{ bg: "gray.50" }}>
                                    <Td {...stickyColCellProps(index % 2 === 0)}>{index + 1}</Td>
                                    <Td fontWeight="bold">{user.fullName}</Td>
                                    <Td>{user.email}</Td>
                                    <Td fontWeight="bold">
                                        <Link href={`tel:${user.phoneNumber}`} display="flex" alignItems="center">
                                            <Icon as={PhoneIcon} mr={2} />
                                            {user.phoneNumber}
                                        </Link>
                                    </Td>
                                    <Td>
                                        <HStack spacing={2}>
                                            <Box as="span" border="1px solid" borderColor="red.200" bg="red.50" borderRadius="lg" p={1}>
                                                <IconButton
                                                    aria-label="Deactivate User"
                                                    icon={<FaUserTimes />}
                                                    color="red.500"
                                                    variant="ghost"
                                                    size="sm"
                                                    _hover={{ bg: "red.100" }}
                                                    onClick={() => openModal(user)}
                                                />
                                            </Box>
                                            <Box as="span" border="1px solid" borderColor="brand.200" bg="brand.50" borderRadius="lg" p={1}>
                                                <IconButton
                                                    aria-label="Edit User"
                                                    icon={<FaEdit />}
                                                    color="brand.600"
                                                    variant="ghost"
                                                    size="sm"
                                                    _hover={{ bg: "brand.100" }}
                                                    onClick={() => openUpdateModal(user)}
                                                />
                                            </Box>
                                            <Box as="span" border="1px solid" borderColor="accent.200" bg="accent.50" borderRadius="lg" p={1}>
                                                <IconButton
                                                    aria-label="Reset Password"
                                                    icon={<FaKey />}
                                                    color="accent.600"
                                                    variant="ghost"
                                                    size="sm"
                                                    _hover={{ bg: "accent.100" }}
                                                    onClick={() => openResetPasswordModal(user)}
                                                />
                                            </Box>
                                        </HStack>
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                </TableContainer>
                </Box>
            )}

            <ConfirmModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={confirmDeactivation}
                message={`Želite li deaktivirati korisnika ${selectedUser?.fullName}?`}
            />

            <CreateUserModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreate={handleCreateUser}
                locationId={locationId}
            />

            <UpdateUserModal
                isOpen={isUpdateModalOpen}
                onClose={() => setIsUpdateModalOpen(false)}
                onUpdate={handleUpdateUser}
                user={selectedUser}
                locationId={locationId}
            />

            <ResetPasswordModal
                isOpen={isResetModalOpen}
                onClose={() => setIsResetModalOpen(false)}
                userId={selectedUser?.id}
                userName={selectedUser?.fullName}
            />
        </>
    );
};

export default UserTable;
